// MA-891 — the pure badge model (KID-JOURNEY-2026-10-08 §4-§5). No UI, no storage.
// Spec: docs/build/specs/MA-891-badge-model.md. MA-892 persists what this derives.

import type { ChildAttempt } from "@/adaptation/types";
import { KID_LABEL_MAP } from "../journeyView";
import { buildBadgeCatalogue, CHAPTER_TROPHY, CHAPTER_SKILL_IDS, CHAPTER_TITLES_KID, type BadgeCatalogue, type CatalogueInput } from "./catalogue";
import { skillBadgeLook, medalLook, BADGE_LOOKS, MEDAL_LOOKS, type BadgeLook, type MedalLook } from "./look";
import { skillStage, engineBandSkillMemo, practiceDayCount, dayBadgeInstances, dayBadgeTimesHeld, trophyEarned, type SkillStage } from "./rules";
import { stageDots, dayRing, skillFraction, closestBadge, practiceDaysSincePreviousDayBadge, type DayRing, type ClosestCandidate } from "./progress";
import { PRACTICE_DAY_THRESHOLDS, chapterBadgeId, daysBadgeId, daysInstanceId, skillBadgeId, skillStageId } from "./ids";

export * from "./ids";
export * from "./rules";
export * from "./catalogue";
export * from "./look";
export * from "./progress";

export interface BookSkillBadge {
  id: string;
  skillId: string;
  chapterId: string;
  kidLabel: string;
  stage: SkillStage;
  dots: 0 | 1 | 2 | 3;
  look: BadgeLook;
  pickEligible: boolean;
  tryIt: boolean;
}
export interface BookTrophy { id: string; chapterId: string; earned: boolean; look: MedalLook }
export interface BookDayBadge { id: string; threshold: number; timesHeld: number; look: MedalLook; ring: DayRing }

export interface BadgeBook {
  catalogue: BadgeCatalogue;
  skills: BookSkillBadge[];
  trophies: BookTrophy[];
  days: BookDayBadge[];
  practiceDays: number;
  practiceDaysSincePrevious: number;
  /** Every badge record held now (skill stages, trophies, day instances). */
  earnedIds: string[];
  closest: ClosestCandidate | null;
}

export interface BadgeBookInput extends Omit<CatalogueInput, "earnedSkillIds"> {
  attempts: readonly ChildAttempt[];
  /** event_at of every session_completed event of the child. */
  sessionCompletedAt: readonly string[];
  /** Badge records already latched (MA-892): `skill:<id>:silver|gold`, trophies, day instances. */
  latchedIds?: ReadonlySet<string>;
  /** The pal's Next-up skill (D-KJ-21). Defaults to the view's pal stone. */
  nextUpSkillId?: string | null;
}

/** Derived-now ∪ latched, with looks, dots, rings and the closest badge. */
export function buildBadgeBook(input: BadgeBookInput): BadgeBook {
  const latched = input.latchedIds ?? new Set<string>();
  // MA-919 (RED round 1 SHOULD-3): one served-pool read per skill per build.
  const ruleOpts = { engineBandSkill: engineBandSkillMemo() };
  const stageOf = (skillId: string): SkillStage => {
    const derived = skillStage(skillId, input.attempts, ruleOpts);
    if (derived === "gold" || latched.has(skillStageId(skillId, "gold"))) return "gold";
    if (derived === "silver" || latched.has(skillStageId(skillId, "silver"))) return "silver";
    return derived;
  };
  // Over EVERY chapter skill (the view never renders a suppressed stone, so an earned,
  // parent-removed skill is only found here). stageOf reads the latch, so a latched
  // record counts; the catalogue holds chapter skills only, so no other id is needed.
  const earnedSkillIds = new Set<string>();
  for (const skillId of CHAPTER_SKILL_IDS) {
    const st = stageOf(skillId);
    if (st === "gold" || st === "silver") earnedSkillIds.add(skillId);
  }
  const catalogue = buildBadgeCatalogue({ ...input, earnedSkillIds });
  const earnedIds: string[] = [];
  const skills: BookSkillBadge[] = catalogue.skills.map((e) => {
    const stage = stageOf(e.skillId);
    if (stage === "silver" || stage === "gold") earnedIds.push(skillStageId(e.skillId, "silver"));
    if (stage === "gold") earnedIds.push(skillStageId(e.skillId, "gold"));
    return {
      id: e.id, skillId: e.skillId, chapterId: e.chapterId, kidLabel: e.kidLabel, stage, dots: stageDots(stage),
      look: skillBadgeLook({ status: e.status, silver: stage === "silver" || stage === "gold", gold: stage === "gold", pickEligible: e.pickEligible, tapEligibility: e.tapEligibility }),
      pickEligible: e.pickEligible, tryIt: e.tryIt,
    };
  });
  const goldSet = new Set(skills.filter((s) => s.stage === "gold").map((s) => s.skillId));
  const trophies: BookTrophy[] = catalogue.trophies.map((t) => {
    const earned = latched.has(t.id) || trophyEarned(t.skillIds, (id) => goldSet.has(id));
    if (earned) earnedIds.push(t.id);
    return { id: t.id, chapterId: t.chapterId, earned, look: medalLook(earned) };
  });
  const count = practiceDayCount(input.sessionCompletedAt);
  const instances = new Set([...dayBadgeInstances(count), ...[...latched].filter((id) => id.startsWith("days:"))]);
  earnedIds.push(...instances);
  const days: BookDayBadge[] = catalogue.days.map((d) => {
    const timesHeld = Math.max(dayBadgeTimesHeld(d.threshold, count), [...instances].filter((id) => id === d.id || id.startsWith(`${d.id}#`)).length);
    return { id: d.id, threshold: d.threshold, timesHeld, look: medalLook(timesHeld > 0), ring: dayRing(d.threshold, count) };
  });
  const candidates: ClosestCandidate[] = [
    ...skills.filter((s) => s.look !== "hidden").map((s) => ({ id: s.id, family: "skill" as const, fraction: skillFraction(s.stage), done: s.stage === "gold" })),
    ...days.map((d) => ({ id: d.id, family: "days" as const, fraction: d.ring.fraction, done: d.timesHeld > 0 && d.threshold !== 7 && d.threshold !== 14, held: d.timesHeld > 0 })),
  ];
  return {
    catalogue, skills, trophies, days, practiceDays: count,
    practiceDaysSincePrevious: practiceDaysSincePreviousDayBadge(count),
    earnedIds, closest: closestBadge(candidates, input.nextUpSkillId !== undefined ? input.nextUpSkillId : input.view.palStoneId),
  };
}

/** Every name string a badge in this module can carry (ids for every chapter skill,
 *  stage records, trophies, day badges and repeat instances up to 100 days, family,
 *  look and stage names, and the kid labels the badges are named by). The
 *  deny-list test runs over this list. */
export function allBadgeNameStrings(): string[] {
  const out = new Set<string>();
  for (const [skillId, label] of Object.entries(KID_LABEL_MAP)) {
    out.add(skillBadgeId(skillId));
    out.add(skillStageId(skillId, "silver"));
    out.add(skillStageId(skillId, "gold"));
    out.add(label);
  }
  for (const trophy of Object.values(CHAPTER_TROPHY)) out.add(chapterBadgeId(trophy));
  for (const title of CHAPTER_TITLES_KID) out.add(title);
  for (const n of PRACTICE_DAY_THRESHOLDS) out.add(daysBadgeId(n));
  for (const n of [7, 14]) for (let k = 1; k <= Math.floor(100 / n); k++) out.add(daysInstanceId(n, k));
  for (const s of ["skill", "chapter", "days", ...BADGE_LOOKS, ...MEDAL_LOOKS, "none", "started", "silver", "gold"]) out.add(s);
  return [...out];
}
